/**
 * TK-39 (lee 23/10) — trang Cài đặt chiến lược: API 32 timer dim · áp lịch cả tuyến · tên nhóm "tên (số)" · đèn chưa xuống nhóm.
 * Luật seek ở `seekDim.ts` (thuần, máy chủ chép y hệt).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { TimerDim } from './seekDim';
import type { TrangThaiLenh } from './nemaApi';

/** Mọi chỗ hiện nhóm ghi "tên (số)" (§4). Nhóm chưa có số (di trú chưa chạy) ⇒ chỉ tên. */
export const tenNhomSo = (g: { name: string; so: number | null }) => (g.so === null ? g.name : `${g.name} (${g.so})`);
export const TEN_NHOM_MAC_DINH = 'Mặc định';

/**
 * Đèn đã mang đúng số nhóm web chưa. `nhom_thiet_bi` NULL + đèn chưa vào nhóm nào (số 0) ⇒ COI LÀ KHỚP: đèn xuất xưởng mang nhóm 0
 * (lee Q4) — chấm vàng trên mọi đèn chưa từng gán nhóm chỉ là nhiễu. NULL + đèn ở nhóm 1..254 ⇒ chưa xuống đèn.
 */
export function chuaXuongDen(soWeb: number, nhomThietBi: number | null): boolean {
  if (nhomThietBi === null) return soWeb !== 0;
  return nhomThietBi !== soWeb;
}

export interface NhomTimer { so: number; name: string; color: string | null; soDen: number; nhanh: number[] }
export interface TimerDimTu {
  nhap: TimerDim[];
  dangChay: { timers: TimerDim[]; luc: string; lenhId: string } | null;
  guiCuoi: { id: string; state: TrangThaiLenh; issuedAt: string; ackAt: string | null } | null;
  khac: boolean;
  nhom: NhomTimer[];
}
export const qkCaiDat = { timerDim: (id: string) => ['cai-dat', 'timer-dim', id] as const };

export function useTimerDim(cabinetId: string | null) {
  return useQuery({
    queryKey: qkCaiDat.timerDim(cabinetId ?? ''),
    queryFn: () => api.get<TimerDimTu>(`/api/nema/tu/${cabinetId}/timer-dim`),
    enabled: !!cabinetId,
    refetchInterval: 5_000,
  });
}
export interface KetApDimTu { cabinetId: string; code: string | null; name: string; lenhId: string; state: TrangThaiLenh; thieuNhom: number[] }
export function useGuiTimerDim(cabinetId: string | null) {
  const qc = useQueryClient();
  const lamMoi = () => { void qc.invalidateQueries({ queryKey: ['cai-dat'] }); };
  const luu = useMutation({ mutationFn: (timers: TimerDim[]) => api.put(`/api/nema/tu/${cabinetId}/timer-dim`, { timers }), onSuccess: lamMoi });
  const gui = useMutation({ mutationFn: (timers: TimerDim[]) => api.post<{ id: string; state: TrangThaiLenh }>(`/api/nema/tu/${cabinetId}/timer-dim/gui`, { timers }), onSuccess: lamMoi });
  const apTuyen = useMutation({
    mutationFn: async (v: { routeId: string; timers: TimerDim[] }) => {
      await api.put(`/api/nema/tu/${cabinetId}/timer-dim`, { timers: v.timers });
      return api.post<{ tu: KetApDimTu[] }>(`/api/nema/tuyen/${v.routeId}/timer-dim/ap`, { cabinetIdNguon: cabinetId });
    },
    onSuccess: lamMoi,
  });
  return { luu, gui, apTuyen };
}

export type KetQuaApTu = 'da-ap' | 'mat-ket-noi' | 'tu-choi' | 'cho-hoi-dap';
export interface KetApLich {
  tong: number; daAp: number; matKetNoi: number; tuChoi: number; choHoiDap: number;
  tu: { cabinetId: string; code: string | null; name: string; ketQua: KetQuaApTu; lyDo?: string }[];
}
export const apLichTuyen = (routeId: string, cabinetIdNguon: string) => api.post<KetApLich>(`/api/routes/${routeId}/ap-lich`, { cabinetIdNguon });
/** "x/y tủ đã áp · z tủ mất kết nối (sẽ áp khi lên mạng) · w tủ từ chối" — vế bằng 0 thì bỏ (trừ vế đầu). */
export function cauApLich(k: KetApLich): string {
  const ve = [`${k.daAp}/${k.tong} tủ đã áp`];
  if (k.matKetNoi) ve.push(`${k.matKetNoi} tủ mất kết nối (sẽ áp khi lên mạng)`);
  if (k.tuChoi) ve.push(`${k.tuChoi} tủ từ chối`);
  if (k.choHoiDap) ve.push(`${k.choHoiDap} tủ chưa hồi đáp`);
  return ve.join(' · ');
}
